'use client';

/**
 * Custom React hook for loading Form.io forms with translations
 *
 * Handles:
 * - Loading form definition from Form.io API
 * - Loading Form.io React module dynamically
 * - Loading translations for current locale
 * - Caching form definitions
 */

import { useState, useEffect, useRef } from 'react';
import { useParams } from 'next/navigation';
import { type FormType } from '@formio/react';
import { DEFAULT_LOCALE } from '@/constants/locales';
import { getTranslationsForLocale } from '@/utils/formio-translation';

let formModulePromise: Promise<typeof import('@formio/react')> | null = null;

function getFormModule() {
  if (!formModulePromise) {
    formModulePromise = import('@formio/react');
  }
  return formModulePromise;
}

const formJsonCache: { [name: string]: FormType } = {};
const fetchPromises: { [name: string]: Promise<FormType> } = {};

function normalizeSlashDateTokens(value: string): string {
  return value
    .replace(/\bMM-DD-YYYY\b/g, 'MM/DD/YYYY')
    .replace(/\bMM-dd-yyyy\b/g, 'MM/dd/yyyy')
    .replace(/\bmm-dd-yyyy\b/g, 'mm/dd/yyyy');
}

function normalizeFormioDateSeparators(form: FormType): FormType {
  const cloned = JSON.parse(JSON.stringify(form)) as Record<string, unknown>;

  const visit = (node: unknown) => {
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }

    if (!node || typeof node !== 'object') return;
    const obj = node as Record<string, unknown>;

    const key = typeof obj.key === 'string' ? obj.key.toLowerCase() : '';
    const isDobLike = key.includes('dateofbirth') || key.includes('dob');

    // Normalize known date display/entry settings.
    for (const field of ['placeholder', 'format', 'displayInTimezone', 'inputFormat']) {
      if (typeof obj[field] === 'string') {
        obj[field] = normalizeSlashDateTokens(obj[field] as string);
      }
    }

    if (typeof obj.inputMask === 'string') {
      obj.inputMask = normalizeSlashDateTokens((obj.inputMask as string).replace(/-/g, '/'));
    }

    if (typeof obj.widget === 'object' && obj.widget !== null) {
      const widget = obj.widget as Record<string, unknown>;
      for (const field of ['format', 'placeholder']) {
        if (typeof widget[field] === 'string') {
          widget[field] = normalizeSlashDateTokens(widget[field] as string);
        }
      }
    }

    // Strong fallback for DOB-like keys that still carry the dashed placeholder.
    if (isDobLike && obj.placeholder === 'MM-DD-YYYY') {
      obj.placeholder = 'MM/DD/YYYY';
    }

    Object.values(obj).forEach(visit);
  };

  visit(cloned);
  return cloned as FormType;
}

function shouldNormalizeDateSeparatorsForForm(name: string): boolean {
  // Keep slash-format normalization limited to Early Ballot flows.
  return /ballot/i.test(name);
}

export function normalizeFormioDateSeparatorsForForm(name: string, form: FormType): FormType {
  return shouldNormalizeDateSeparatorsForForm(name)
    ? normalizeFormioDateSeparators(form)
    : form;
}

// Cache Form.io Form JSON in-memory
function fetchFormJson(name: string): Promise<FormType> {
  if (formJsonCache[name]) {
    return Promise.resolve(formJsonCache[name]);
  }

  if (!fetchPromises[name]) {
    fetchPromises[name] = fetch(`/api/formio-form?name=${encodeURIComponent(name)}`)
      .then((res) => res.json())
      .then((json) => {
        const normalized = normalizeFormioDateSeparatorsForForm(name, json as FormType);
        formJsonCache[name] = normalized;
        return normalized;
      })
      .finally(() => {
        delete fetchPromises[name];
      });
  }

  return fetchPromises[name];
}

export interface UseFormioFormOptions {
  /**
   * Form.io form path to fetch the definition for. Optional: omit (or pass an
   * empty string) when the form schema is supplied from elsewhere — e.g. the
   * Contentstack Form.io marketplace app — in which case no fetch is made and
   * `formDefinition` stays null. Translations and the Form.io module still load.
   */
  formName?: string;
  autoPreload?: boolean;
}

export interface UseFormioFormReturn {
  formDefinition: FormType | null;
  translations: Record<string, string>;
  currentLocale: string;
  isLoading: boolean;
  isModuleLoading: boolean;
  isLoadingTranslations: boolean;
  eventHandlerAttached: React.RefObject<boolean>;
}

/**
 * Custom hook for loading Form.io forms with translations
 *
 * @param options - Configuration options
 * @returns Form state and loading flags
 *
 * @example
 * const { formDefinition, translations, currentLocale, isLoading } = useFormioForm({
 *   formUrl: 'https://example.form.io/myform'
 * });
 */
export function useFormioForm(options: UseFormioFormOptions): UseFormioFormReturn {
  const { formName, autoPreload = true } = options;
  // Only fetch a form definition when a form name is provided. When the schema
  // is supplied directly (e.g. Contentstack marketplace app), fetching is off.
  const shouldFetchForm = Boolean(formName);
  const params = useParams();
  const currentLocale = (params.locale as string) || DEFAULT_LOCALE;

  const [formDefinition, setFormDefinition] = useState<FormType | null>(
    formName ? formJsonCache[formName] || null : null
  );
  const [isModuleLoading, setIsModuleLoading] = useState(true);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(true);
  const eventHandlerAttached = useRef(false);

  // Load translations when locale changes
  useEffect(() => {
    let isCancelled = false;

    const loadTranslations = async () => {
      setIsLoadingTranslations(true);
      try {
        const trans = await getTranslationsForLocale(currentLocale);
        if (!isCancelled) {
          setTranslations(trans);
        }
      } catch (error) {
        console.error('[useFormioForm] Error loading translations:', error);
      } finally {
        if (!isCancelled) {
          setIsLoadingTranslations(false);
        }
      }
    };

    loadTranslations();

    return () => {
      isCancelled = true;
    };
  }, [currentLocale]);

  // Load the Form.io React module. Needed to render the form regardless of
  // where the schema comes from, so this is independent of `formName`.
  useEffect(() => {
    let isCancelled = false;
    getFormModule().then(() => {
      if (!isCancelled) {
        setIsModuleLoading(false);
      }
    });
    return () => {
      isCancelled = true;
    };
  }, []);

  // Optionally load the form definition. Skipped entirely when no `formName` is
  // provided. Depends only on `formName` — keeping `formDefinition` out of the
  // deps prevents the effect from re-running (and re-fetching) once loaded; the
  // in-flight dedupe in fetchFormJson collapses this with the preload effect.
  useEffect(() => {
    if (!formName) return;

    let isCancelled = false;
    fetchFormJson(formName).then((json) => {
      if (!isCancelled) {
        setFormDefinition(json);
      }
    });
    return () => {
      isCancelled = true;
    };
  }, [formName]);

  // Preload form module (and, when applicable, the definition) on the client.
  useEffect(() => {
    if (!autoPreload || typeof window === 'undefined') return;
    getFormModule();
    if (formName) {
      fetchFormJson(formName);
    }
  }, [formName, autoPreload]);

  const isLoading =
    isModuleLoading || isLoadingTranslations || (shouldFetchForm && !formDefinition);

  return {
    formDefinition,
    translations,
    currentLocale,
    isLoading,
    isModuleLoading,
    isLoadingTranslations,
    eventHandlerAttached,
  };
}

// Export utility functions for direct use if needed
export { getFormModule, fetchFormJson };
